import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  content: ReactNode;
  children: ReactElement;
  placement?: "top" | "right" | "bottom" | "left";
  delay?: number;
}

const VIEWPORT_MARGIN = 8;

function computeStyle(
  rect: DOMRect,
  placement: NonNullable<TooltipProps["placement"]>,
): CSSProperties {
  switch (placement) {
    case "bottom":
      return {
        top: rect.bottom + VIEWPORT_MARGIN,
        left: clampX(rect.left + rect.width / 2),
        transform: "translateX(-50%)",
      };
    case "left":
      return {
        top: rect.top + rect.height / 2,
        left: Math.max(VIEWPORT_MARGIN, rect.left - VIEWPORT_MARGIN),
        transform: "translate(-100%, -50%)",
      };
    case "right":
      return {
        top: rect.top + rect.height / 2,
        left: Math.min(
          window.innerWidth - VIEWPORT_MARGIN,
          rect.right + VIEWPORT_MARGIN,
        ),
        transform: "translateY(-50%)",
      };
    case "top":
    default:
      return {
        top: Math.max(VIEWPORT_MARGIN, rect.top - VIEWPORT_MARGIN),
        left: clampX(rect.left + rect.width / 2),
        transform: "translate(-50%, -100%)",
      };
  }
}

function clampX(x: number) {
  // Evita que el tooltip centrado se salga por los bordes laterales.
  const half = 130; // ~max-w-[260px] / 2
  return Math.min(
    Math.max(x, half + VIEWPORT_MARGIN),
    window.innerWidth - half - VIEWPORT_MARGIN,
  );
}

/**
 * Tooltip renderizado en portal con posición fija: nunca queda recortado
 * por contenedores con overflow oculto (cards, tablas, modales).
 */
export function Tooltip({
  content,
  children,
  placement = "top",
  delay = 300,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<CSSProperties>({});
  const triggerRef = useRef<HTMLSpanElement>(null);
  const timerRef = useRef<number | undefined>(undefined);

  function show() {
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setStyle(computeStyle(rect, placement));
      setOpen(true);
    }, delay);
  }

  function hide() {
    window.clearTimeout(timerRef.current);
    setOpen(false);
  }

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return (
    <span
      ref={triggerRef}
      className="inline-flex"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {open &&
        createPortal(
          <span
            role="tooltip"
            style={style}
            className="pointer-events-none fixed z-[80] max-w-[260px] rounded-md border border-border-strong bg-elevated px-2 py-1 text-xs leading-snug text-text shadow-lg"
          >
            {content}
          </span>,
          document.body,
        )}
    </span>
  );
}
